import {
  Alert, Button, Checkbox, Container, Fieldset, Grid, Group, LoadingOverlay,
  Select, Stack, Text, Textarea, TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconEdit, IconRotate, IconX } from '@tabler/icons-react';
import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDirtyFormGuard, useFormSubmitSaver } from '../hooks/useDirtyFormGuard';
import { StickyDetailHeader } from '../components/StickyHeader';
import { useAlertModal } from '../components/AlertModalProvider';

import { ApiError } from '../api/client';
import { applyApiErrorToForm, isFieldLevelApiError } from '../utils/applyApiError';
import type {
  AgreementPartyCreateInput, AgreementPartyUpdateInput, BeneficiarySubtype,
  PartyRole, RepresentativePower,
} from '../api/types';
import { AgreementPartyPicker, AgreementPicker, PersonPicker } from '../components/EntityPicker';
import {
  useAgreementParty, useCreateAgreementParty,
  useRestoreAgreementParty, useUpdateAgreementParty,
} from '../hooks/useAgreementParties';
import { useAgreement } from '../hooks/useAgreements';

const ROLE_OPTS = [
  { value: 'HOLDER', label: 'Holder' },
  { value: 'REPRESENTATIVE', label: 'Representative' },
  { value: 'BENEFICIARY', label: 'Beneficiary' },
];
const POWER_OPTS = [
  { value: 'LPOA', label: 'LPOA (natural-person holder)' },
  { value: 'MANDATO', label: 'Mandato (legal-entity holder)' },
];
const SUBTYPE_OPTS = [
  { value: '', label: '-' },
  { value: 'LEGAL_HEIR', label: 'Legal heir' },
  { value: 'WILL_HEIR', label: 'Heir under will' },
];

interface FormValues {
  agreement_id: string;
  person_id: string;
  role: PartyRole;
  related_party_id: string;
  is_ubo: boolean;
  beneficiary_subtype: string;
  representative_power_type: string;
  power_of_attorney_reference: string;
  notes: string;
}

const EMPTY: FormValues = {
  agreement_id: '', person_id: '', role: 'HOLDER',
  related_party_id: '', is_ubo: false, beneficiary_subtype: '',
  representative_power_type: '', power_of_attorney_reference: '', notes: '',
};

export function AgreementPartyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const { data, isLoading } = useAgreementParty(isNew ? undefined : id);
  const create = useCreateAgreementParty();
  const update = useUpdateAgreementParty(id ?? '');
  const restore = useRestoreAgreementParty();

  const presetAgreementId = isNew ? (searchParams.get('agreement_id') ?? '') : '';
  // D2-polish 2: when returning from a [+ Create new person] flow, the new
  // person's id is appended to the URL as ?person_id=<id>; pre-fill the
  // picker so the user does not have to re-pick.
  const presetPersonId = isNew ? (searchParams.get('person_id') ?? '') : '';

  const initial: FormValues = isNew || !data ? {
    ...EMPTY, agreement_id: presetAgreementId, person_id: presetPersonId,
  } : {
    agreement_id: data.agreement_id,
    person_id: data.person_id,
    role: data.role,
    related_party_id: data.related_party_id ?? '',
    is_ubo: data.is_ubo,
    beneficiary_subtype: data.beneficiary_subtype ?? '',
    representative_power_type: data.representative_power_type ?? '',
    power_of_attorney_reference: data.power_of_attorney_reference ?? '',
    notes: data.notes ?? '',
  };

  const form = useForm<FormValues>({
    initialValues: initial,
    validate: {
      agreement_id: (v) => (v.length >= 32 ? null : 'Agreement required'),
      person_id: (v) => (v.length >= 32 ? null : 'Person required'),
      representative_power_type: (v, all) =>
        all.role === 'REPRESENTATIVE' && !v ? 'Required for representatives' : null,
    },
  });
  // Pedro 2026-10-10: leaving with unsaved edits offers "Save & leave" (runs this page's Save).
  const saveForGuard = useFormSubmitSaver('agreement-party-form', form.isDirty(), !isNew);
  const confirmDiscard = useDirtyFormGuard(form.isDirty(), saveForGuard);
  const [editMode, setEditMode] = React.useState(isNew);
  const { showAlert } = useAlertModal();

  if (!isNew && data && form.values.agreement_id === '' && data.agreement_id !== '') {
    form.setValues(initial);
    form.resetDirty(initial);
  }

  const _err = isNew ? create.error : update.error;
  const apiError = (_err instanceof ApiError && !isFieldLevelApiError(_err))
    ? _err.detail : null;
  const isDeleted = !!data?.deleted_at;
  const role = form.values.role;

  // Pedro 2026-05-04 round-6: parent agreement is needed to enforce
  // the UBO-only-on-Empresa rule at the UI layer. The server enforces
  // it too — see _enforce_ubo_only_on_empresa in routers/agreement_parties.py.
  // Hook returns undefined until an agreement is selected (create
  // mode without preset) or loaded (edit mode), at which point we
  // know whether to show the UBO checkbox. parentClientType ===
  // 'Empresa' is the strict positive condition; anything else
  // (including null/undefined while loading) hides UBO.
  const parentAgreement = useAgreement(form.values.agreement_id || undefined);
  const parentClientType = parentAgreement.data?.client_type;
  const uboAllowed = parentClientType === 'Empresa';

  function handleSubmit(values: FormValues) {
    if (isNew) {
      const payload: AgreementPartyCreateInput = {
        agreement_id: values.agreement_id,
        person_id: values.person_id,
        role: values.role,
        is_ubo: values.is_ubo,
      };
      attachOptional(payload as unknown as Record<string, any>, values);
      create.mutate(payload, {
        onSuccess: (r) => {
          form.resetDirty();
          // Bug fix (2026-06-09 during A1.f): see AgreementDetailPage
          // create.mutate onSuccess for the rationale. Flip editMode
          // explicitly so the fallback navigate (`/agreement-parties/${id}`)
          // lands in read mode. Harmless on returnTo/change-requests paths
          // (component unmounts).
          setEditMode(false);
          if (r.pending) {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: 'Your new role assignment was filed. It is not yet visible — Compliance must approve it first.',
            });
            navigate(`/change-requests/${r.changeRequestId}`);
          } else {
            notifications.show({ message: 'Role assignment created', color: 'green' });
            // D2-polish 2: honour return_to so "+ Add Party" → create person
            // → save party leaves the user back on the originating agreement.
            const returnTo = searchParams.get('return_to');
            if (returnTo) {
              navigate(returnTo, { replace: true });
            } else {
              navigate(`/agreement-parties/${r.entity.id}`, { replace: true });
            }
          }
        },
        onError: (err) => { applyApiErrorToForm(form, err); },
      });
    } else {
      // Pedro 2026-05-04 round-6: include role in the PATCH payload
      // (was previously omitted because role was locked at creation).
      // is_ubo is force-set to false when role isn't BENEFICIARY so
      // a HOLDER → BENEFICIARY → HOLDER transition cleans up the
      // is_ubo flag automatically (DB CHECK ap_holder_no_extras would
      // reject otherwise).
      const payload: AgreementPartyUpdateInput = {
        role: values.role,
        is_ubo: values.role === 'BENEFICIARY' ? values.is_ubo : false,
      };
      attachOptional(payload as unknown as Record<string, any>, values);
      update.mutate(payload, {
        onSuccess: (r) => {
          form.resetDirty(values);
          setEditMode(false);
          if (r.pending) {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: 'Your edits were filed. The live record is unchanged until Compliance approves.',
            });
          } else {
            notifications.show({ message: 'Saved', color: 'green' });
          }
        },
        onError: (err) => { applyApiErrorToForm(form, err); },
      });
    }
  }

  return (
    <Container size="xl">
      <Stack>
        <StickyDetailHeader
          editing={editMode}
          title={isNew ? 'New role assignment' : `${data?.role ?? '...'} assignment`}
          backOnClick={() => confirmDiscard(() => {
            // #391 sweep (2026-06-09): clear dirty state BEFORE navigating
            // so the passive useBlocker (DirtyFormProvider, #258) doesn't
            // re-trigger the discard modal on the navigate.
            form.setValues(initial);
            form.resetDirty(initial);
            setEditMode(false);
            navigate(-1);
          })}
          primary={
            !editMode ? (
              <Button leftSection={<IconEdit size={16} />} onClick={() => setEditMode(true)}>
                Edit
              </Button>
            ) : (
              <Group gap="xs">
                <Button variant="default" leftSection={<IconX size={16} />}
                  onClick={() => {
                    confirmDiscard(() => {
                      form.setValues(initial);
                      form.resetDirty(initial);
                      setEditMode(false);
                    });
                  }}>
                  Cancel
                </Button>
                <Button form="agreement-party-form" type="submit"
                  loading={create.isPending || update.isPending}
                  disabled={!form.isDirty() && !isNew}>
                  {isNew ? 'Save' : 'Save changes'}
                </Button>
              </Group>
            )
          }
          destructive={
            /* #8 (2026-06-16, Pedro): the role-assignment page edits the
               role only — no Delete here. Breaking the link is done from
               the relationship list (chain icon, in line with the record).
               Restore stays for a soft-deleted assignment (no Edit mode). */
            !isNew && data && isDeleted ? (
              <Button variant="light" color="green" leftSection={<IconRotate size={16} />}
                onClick={() => restore.mutate(data.id)} loading={restore.isPending}>Restore</Button>
            ) : null
          }
        />

        {apiError && <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>{apiError}</Alert>}

        <form id="agreement-party-form" onSubmit={form.onSubmit(handleSubmit)}>
          <Stack pos="relative">
            <LoadingOverlay visible={isLoading} />
            <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
              <Fieldset legend="Linkage (immutable after creation)">
                <Grid>
                  <Grid.Col span={6}>
                    <AgreementPicker
                      label="Agreement" required
                      disabled={!isNew || !!presetAgreementId}
                      value={form.values.agreement_id}
                      onChange={(v) => form.setFieldValue('agreement_id', v ?? '')}
                    />
                    {isNew && presetAgreementId && (
                      <Text size="xs" c="dimmed" mt={2}>Locked — adding from this agreement</Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <PersonPicker
                      label="Person" required
                      // D2-polish 2 follow-up: when we just landed back from
                      // a [+ Create new person] flow, lock the picker so the
                      // user does not change horse mid-stream.
                      disabled={!isNew || !!presetPersonId}
                      value={form.values.person_id}
                      onChange={(v) => form.setFieldValue('person_id', v ?? '')}
                      onCreateNew={isNew && !presetPersonId ? () => {
                        // D2-polish 2: navigate to /persons/new with a
                        // return_to that comes back here, so the new person's
                        // id flows into the picker on save.
                        const here = window.location.pathname + window.location.search;
                        const target = `/persons/new?return_to=${encodeURIComponent(here)}&return_field=person_id`;
                        navigate(target);
                      } : undefined}
                    />
                    {isNew && presetPersonId && (
                      <Text size="xs" c="dimmed" mt={2}>Locked — person pre-selected</Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={6}>
                    {/* Pedro 2026-05-04 round-6: role is now mutable
                        (was previously locked after creation). The
                        backend PATCH schema accepts role; the audit
                        log captures the transition. The maker-checker
                        flow still applies — a Compliance approval is
                        required before the live row reflects the
                        change. */}
                    <Select label="Role" data={ROLE_OPTS} {...form.getInputProps('role')} allowDeselect={false} />
                  </Grid.Col>
                  {(role === 'REPRESENTATIVE' || role === 'BENEFICIARY') && form.values.agreement_id && (
                    <Grid.Col span={6}>
                      <AgreementPartyPicker
                        label="Related party"
                        agreementId={form.values.agreement_id}
                        value={form.values.related_party_id}
                        onChange={(v) => form.setFieldValue('related_party_id', v ?? '')}
                      />
                      <Text size="xs" c="dimmed" mt={2}>
                        {role === 'REPRESENTATIVE'
                          ? 'The HOLDER row this representative acts for'
                          : 'The HOLDER row this beneficiary derives from'}
                      </Text>
                    </Grid.Col>
                  )}
                </Grid>
              </Fieldset>

              {role === 'REPRESENTATIVE' && (
                <Fieldset legend="Representative details">
                  <Stack>
                    <Select label="Power type" data={POWER_OPTS} {...form.getInputProps('representative_power_type')} allowDeselect={false} />
                    <TextInput label="Power-of-attorney reference" {...form.getInputProps('power_of_attorney_reference')} />
                  </Stack>
                </Fieldset>
              )}

              {role === 'BENEFICIARY' && (
                <Fieldset legend="Beneficiary details">
                  <Stack>
                    {/* Pedro 2026-05-04 round-6: UBO checkbox visible
                        only when the parent agreement is type Empresa.
                        UBO is by definition a legal-entity concept
                        (a natural person cannot have an Ultimate
                        Beneficial Owner — they ARE the ultimate
                        owner). Particular agreements can still have
                        BENEFICIARY-role parties (heirs, designated
                        beneficiaries) — they just cannot carry the
                        UBO flag. The server enforces this rule too;
                        if the user somehow submits is_ubo=true on a
                        Particular agreement, the API returns 422 with
                        code='ubo_requires_empresa'. */}
                    {uboAllowed ? (
                      <Checkbox label="Ultimate Beneficial Owner (UBO)" {...form.getInputProps('is_ubo', { type: 'checkbox' })} />
                    ) : parentClientType === 'Particular' ? (
                      <Text size="xs" c="dimmed">
                        UBO designation does not apply to Particular
                        agreements (a natural person is themselves the
                        ultimate owner).
                      </Text>
                    ) : null}
                    <Select label="Subtype" data={SUBTYPE_OPTS} {...form.getInputProps('beneficiary_subtype')} />
                  </Stack>
                </Fieldset>
              )}

              <Textarea label="Notes" autosize minRows={2} {...form.getInputProps('notes')} />
            </fieldset>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}

function attachOptional(payload: Record<string, any>, values: FormValues): void {
  if (values.related_party_id) payload.related_party_id = values.related_party_id;
  if (values.beneficiary_subtype) payload.beneficiary_subtype = values.beneficiary_subtype as BeneficiarySubtype;
  if (values.representative_power_type) payload.representative_power_type = values.representative_power_type as RepresentativePower;
  if (values.power_of_attorney_reference) payload.power_of_attorney_reference = values.power_of_attorney_reference;
  if (values.notes) payload.notes = values.notes;
}
